/* Shared by both frontends — they report into the same Sentry project, so the noise they have to
   drop is the same. Both reach it through a `shared` alias, of their bundler and of tsconfig
   respectively, which is why this stays plain JavaScript: webpack 4 has no loader for TypeScript. */

import {flatMap, startsWith, values} from './legacy';

/* Extensions carry their own protocol in the URLs they report, Safari masking its own as
   "webkit-masked-url://hidden/". `denyUrls` cannot match those: the stack parser knows no such
   protocol and splits the URL across the frame's filename and function, so both have to be
   checked. */
const ExtensionURLPattern = /(webkit-masked-url|safari-web-extension|chrome-extension|moz-extension):/i;

const isExtensionFrame = frame => (
    ExtensionURLPattern.test(frame.filename || '')
    || ExtensionURLPattern.test(frame.function || '')
);

/* WebKit throws objects that name their source file, and sentry attaches them as extra data. That
   is all an extension leaves behind when the browser reports its exception without a stack. */
const isExtensionSource = extra => Boolean(extra) && ExtensionURLPattern.test(extra.sourceURL || '');

const getValues = event => (event.exception && event.exception.values) || [];

const getFrames = event => flatMap(
    getValues(event),
    value => (value.stacktrace && value.stacktrace.frames) || [],
);

/* Third-party noise that runs from our own URL, so `denyUrls` cannot catch it. Every entry has to
   name something no code of ours would ever say — these match the message alone, with no stack to
   tell us where it came from. */
export const IgnoreErrors = [
    /* Benign rendering notification, not an actual error. */
    /ResizeObserver loop/,
    /* WebExtension APIs (browser./runtime. calls) injected by extensions. */
    /Invalid call to (browser|runtime)\./,
    /* Obfuscated code: terser names ours `t` and `e`, never `_0x4efa94`. */
    /_0x[0-9a-f]{4,}/,
    /* Page translation moves nodes out from under the framework rendering them. */
    'The object can not be found here',
    /* Password managers reading their own tab state. */
    'customFillData',
    /* Cashback extensions asking a response of ours for their reminder. */
    'cashbackReminder',
    /* Extensions replacing built-ins the engine will not let them replace, the user agent among
       them — they rewrite it to hide the browser. */
    /(Cannot|can't) redefine (non-configurable )?property/,
    /* The bridge an iOS host app puts into its webview, and what it answers when nothing is on the
       other end of it. */
    /webkit\.messageHandlers/,
    'WKWebView API client did not respond',
    /* Google's tag manager throws its own minified names. Ours are minified too, but a message of
       nothing but a letter or three is never one of ours: we throw sentences. */
    /^[A-Za-z]{1,3}$/,
    /* Safari's own wording for a message no extension came back to answer. */
    'TimeoutError: operation timed out',
    /* A rejection carrying neither message nor stack — there is nothing in it to look into. */
    'UnhandledRejection: {}',
    'Non-Error promise rejection captured',
    /* The buyer submitted the PayPal form twice; the first submission had gone through. */
    'PAYMENT_ALREADY_DONE',
];

/* Third-party code we can recognise by where it is loaded from. */
export const DenyURLs = [
    /* Safari extensions carry their own bundle path, e.g. PayPal Honey. */
    /\.safariextension\//i,
    /* Extensions of the other browsers name their protocol, which the stack parser keeps intact. */
    /^(chrome|moz)-extension:/,
    /* Analytics we load but do not maintain. */
    /googletagmanager\.com|google-analytics\.com/,
];

export const isThirdPartyEvent = event => (
    getFrames(event).some(isExtensionFrame)
    || values(event.extra || {}).some(isExtensionSource)
);

/* Code run from a string has no file to name, and we never run any: no eval, no new Function, and
   the inline scripts of our templates report the page they sit in. So whatever threw here was put
   into the page by something else — an app hosting us in its webview, usually. The innermost frame
   is the one that threw; the frames above it are ours by the time sentry wraps a handler. */
export const isAnonymousStack = event => {
    const frames = getFrames(event);
    const innermost = frames[frames.length - 1];

    return Boolean(innermost) && (innermost.filename || '') === '<anonymous>';
};

export const matchesError = (event, pattern) => getValues(event).some(value => pattern.test(value.value || ''));

/* A page saved to disk keeps loading our scripts from our domain and reports their errors, but
   misses everything the template inlines. Nothing we could fix from here. */
export const isLocalCopy = event => startsWith((event.request && event.request.url) || '', 'file:');

/* Running out of stack is reported by Chrome on iOS and by the Google app, never by Mobile Safari
   on the same engine — those two inject scripts of their own, which would explain it. But our own
   rendering could just as well be what runs out of room there, so these events carry what the page
   held at the time. `__gCrWeb` is the bridge those two put on the window. */
const StackOverflowPattern = /Maximum call stack size exceeded/i;

const isStackOverflow = event => getValues(event).some(value => StackOverflowPattern.test(value.value || ''));

const describePage = () => ({
    elements: document.querySelectorAll('*').length,
    cells: document.querySelectorAll('td').length,
    body: document.body.className,
    /* eslint-disable-next-line no-underscore-dangle */
    injected: Boolean(window.__gCrWeb),
});

/* A script that fails to parse is reported without a stack and without a file, which leaves nothing
   to tell whose script it was. The scripts the document holds do tell: ours are there under their
   hashed names, and anything else came from the browser the visitor brought. Should one of ours be
   missing from a page that reports this, it is ours to fix after all. */
const isUnplaceableSyntaxError = event => (
    getValues(event).some(value => value.type === 'SyntaxError')
    && !getFrames(event).length
);

const describeScripts = () => Array.prototype.map.call(
    document.scripts,
    script => script.src || '<inline>',
);

/* Both of these are errors we cannot place from the report alone, and no event is ever both. */
export const addExtraContext = event => {
    if (typeof document === 'undefined') return event;

    if (isStackOverflow(event)) {
        event.extra = {...event.extra, page: describePage()};
    } else if (isUnplaceableSyntaxError(event)) {
        event.extra = {...event.extra, scripts: describeScripts()};
    }

    return event;
};
